import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { getAvailableSlots, bookVisit } from '@/lib/portal-api-client';

interface BookVisitModalProps {
  isOpen: boolean;
  onClose: () => void;
  membershipId: string;
  visitsRemaining: number;
  onBookingComplete: () => void;
}

export function BookVisitModal({
  isOpen,
  onClose,
  membershipId,
  visitsRemaining,
  onBookingComplete,
}: BookVisitModalProps) {
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedWindow, setSelectedWindow] = useState('');
  const [visitType, setVisitType] = useState<'pm' | 'repair' | 'emergency'>('pm');
  const [notes, setNotes] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Load available slots when modal opens
  useEffect(() => {
    if (isOpen && membershipId) {
      loadAvailableSlots();
      setSuccess(false);
      setError('');
      setSelectedDate('');
      setSelectedWindow('');
      setNotes('');
    }
  }, [isOpen, membershipId]);

  async function loadAvailableSlots() {
    setLoading(true);
    try {
      const result = await getAvailableSlots(membershipId, 30);
      setAvailableSlots(result.availableSlots || []);
    } catch (err: any) {
      setError('Failed to load available appointment times');
      console.error('Failed to load slots:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    
    if (!selectedDate || !selectedWindow) {
      setError('Please select a date and time window');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await bookVisit(membershipId, {
        visit_date: selectedDate,
        visit_window: selectedWindow,
        visit_type: visitType,
        notes: notes || undefined,
      });

      setSuccess(true);
      setTimeout(() => {
        onBookingComplete();
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to book visit. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  // Get unique dates from available slots
  const uniqueDates = [...new Set(availableSlots.map((s) => s.date))].sort();

  // Get windows for selected date
  const windowsForDate = availableSlots
    .filter((s) => s.date === selectedDate && s.available)
    .map((s) => ({
      key: s.window,
      label: s.windowLabel || s.window,
    }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Book a Visit</h2>
              <p className="text-sm text-gray-600">Schedule your service appointment</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Success State */}
        {success && (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Visit Booked!</h3>
            <p className="text-gray-600">
              Your service appointment has been scheduled successfully.
            </p>
          </div>
        )}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Visit Type Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Visit Type
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setVisitType('pm')}
                  disabled={visitsRemaining === 0}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    visitType === 'pm'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${visitsRemaining === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="font-semibold">PM Visit</div>
                  <div className="text-xs mt-1">
                    {visitsRemaining > 0
                      ? `${visitsRemaining} remaining`
                      : 'None remaining'}
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setVisitType('repair')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    visitType === 'repair'
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold">Repair</div>
                  <div className="text-xs mt-1">Pay as needed</div>
                </button>

                <button
                  type="button"
                  onClick={() => setVisitType('emergency')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    visitType === 'emergency'
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold">Emergency</div>
                  <div className="text-xs mt-1">24/7 service</div>
                </button>
              </div>
            </div>

            {/* Date Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Select Date
              </label>
              {loading && !availableSlots.length ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Loading available dates...</span>
                </div>
              ) : uniqueDates.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">No available dates at this time</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {uniqueDates.slice(0, 20).map((date) => {
                    const dateObj = new Date(date);
                    const isSelected = selectedDate === date;
                    return (
                      <button
                        key={date}
                        type="button"
                        onClick={() => {
                          setSelectedDate(date);
                          setSelectedWindow('');
                        }}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="text-xs text-gray-600 uppercase">
                          {dateObj.toLocaleDateString('en-US', { weekday: 'short' })}
                        </div>
                        <div className="text-lg font-bold mt-0.5">
                          {dateObj.getDate()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {dateObj.toLocaleDateString('en-US', { month: 'short' })}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Time Window Selection */}
            {selectedDate && windowsForDate.length > 0 && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Select Time Window
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {windowsForDate.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSelectedWindow(key)}
                      className={`p-4 rounded-xl border-2 transition-all text-center font-semibold ${
                        selectedWindow === key
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-semibold text-gray-700 mb-2">
                Additional Notes (Optional)
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Any specific concerns or requests..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 px-6 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !selectedDate || !selectedWindow}
                className="flex-1 py-3 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Booking...
                  </>
                ) : (
                  <>
                    <Calendar className="w-5 h-5" />
                    Book Appointment
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
